/**
 * Google Analytics 4 utility functions
 * 
 * Usage:
 * import { trackEvent, trackPageView } from '../utils/analytics';
 * 
 * Track events:
 * trackEvent('contact_form_submit', { method: 'google_auth' });
 * trackEvent('gallery_view', { category: 'exterior_views', image_index: 1 });
 * trackEvent('cta_click', { button: 'request_details' });
 */

// Check if GA is available and configured
const isGAAvailable = () => {
  return typeof window !== 'undefined' && 
         typeof window.gtag === 'function' && 
         process.env.REACT_APP_GA_MEASUREMENT_ID && 
         process.env.REACT_APP_GA_MEASUREMENT_ID !== '';
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event (e.g., 'contact_form_submit')
 * @param {object} params - Additional parameters for the event
 */
export const trackEvent = (eventName, params = {}) => {
  if (isGAAvailable()) {
    window.gtag('event', eventName, params);
  }
};

/**
 * Track a page view (useful for SPA navigation)
 * @param {string} pagePath - The page path
 * @param {string} pageTitle - The page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (isGAAvailable()) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
};

/**
 * Track contact form submission
 * @param {string} method - The submission method (e.g., 'google_auth')
 */
export const trackContactSubmit = (method = 'google_auth') => {
  trackEvent('generate_lead', {
    event_category: 'engagement',
    event_label: 'contact_form',
    method: method,
  });
};

/**
 * Track gallery interaction
 * @param {string} category - Gallery category name
 * @param {number} imageIndex - Index of the image viewed
 */
export const trackGalleryView = (category, imageIndex) => {
  trackEvent('view_item', {
    event_category: 'gallery',
    event_label: category,
    image_index: imageIndex,
  });
};

/**
 * Track CTA button clicks
 * @param {string} buttonName - Name of the button clicked
 */
export const trackCTAClick = (buttonName) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: buttonName,
  });
};

/**
 * Track language change
 * @param {string} language - The selected language code
 */
export const trackLanguageChange = (language) => {
  trackEvent('select_content', {
    event_category: 'language',
    content_type: 'language_preference',
    item_id: language,
  });
};

/**
 * Track PDF download
 */
export const trackPDFDownload = () => {
  trackEvent('file_download', {
    event_category: 'engagement',
    event_label: 'property_brochure',
    file_extension: 'pdf',
  });
};

/**
 * Track viewing schedule click
 */
export const trackViewingSchedule = () => {
  trackEvent('begin_checkout', {
    event_category: 'engagement',
    event_label: 'schedule_viewing',
  });
};

export default {
  trackEvent,
  trackPageView,
  trackContactSubmit,
  trackGalleryView,
  trackCTAClick,
  trackLanguageChange,
  trackPDFDownload,
  trackViewingSchedule,
};
